import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { uploadIcon, isValidIconName } from '../../services/iconService';
import { clearCustomIconsCache } from '../../services/iconSearchService';
import UiIcon from '../ui/UiIcon';

const MIN_REGION = 16;

const GenerationResults = ({ imageUrl, onIconsSaved }) => {
  const [cropRegions, setCropRegions] = useState([]);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [vectorizedResults, setVectorizedResults] = useState([]);
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingIcons, setSavingIcons] = useState({});
  const [savedIcons, setSavedIcons] = useState({});
  const [iconNames, setIconNames] = useState({});
  const [draft, setDraft] = useState(null);

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const nextId = useRef(1);
  const functions = getFunctions();

  const vectorizeRegions = useCallback(async (regions, natural, display) => {
    if (regions.length === 0) {
      setError('Draw at least one crop region on the image');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const scale = natural.width / display.width;
      const actualCropRegions = regions.map(region => ({
        x: Math.round(region.x * scale),
        y: Math.round(region.y * scale),
        width: Math.max(1, Math.round(region.width * scale)),
        height: Math.max(1, Math.round(region.height * scale)),
      }));

      const vectorize = httpsCallable(functions, 'vectorizeImages');
      const result = await vectorize({ imageUrl, cropRegions: actualCropRegions });

      setVectorizedResults(result.data.svgs.map((svgData) => ({
        cropId: regions[svgData.cropIndex].id,
        svg: svgData.svg,
      })));
      setStale(false);
    } catch (err) {
      console.error('Error vectorizing images:', err);
      setError(err.message || 'Failed to vectorize images');
    } finally {
      setLoading(false);
    }
  }, [functions, imageUrl]);

  // Load the image, start with the whole image selected and vectorize it right away
  useEffect(() => {
    if (!imageUrl) return;

    const img = new Image();
    img.onload = () => {
      const natural = { width: img.naturalWidth, height: img.naturalHeight };
      const maxWidth = 440;
      const scale = natural.width > maxWidth ? maxWidth / natural.width : 1;
      const display = { width: natural.width * scale, height: natural.height * scale };
      const full = [{ id: nextId.current++, x: 0, y: 0, width: display.width, height: display.height }];

      setImageSize(natural);
      setDisplaySize(display);
      setCropRegions(full);
      vectorizeRegions(full, natural, display);
    };
    img.src = imageUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  const regionsChanged = (regions) => {
    setCropRegions(regions);
    setStale(true);
  };

  const removeCropRegion = (id) => {
    regionsChanged(cropRegions.filter(region => region.id !== id));
    setVectorizedResults(vectorizedResults.filter(result => result.cropId !== id));
    const newNames = { ...iconNames };
    delete newNames[id];
    setIconNames(newNames);
  };

  const updateCropRegion = (id, newPosition, newSize) => {
    regionsChanged(cropRegions.map(region =>
      region.id === id
        ? { ...region, x: newPosition.x, y: newPosition.y, width: newSize.width, height: newSize.height }
        : region
    ));
  };

  // Grid templates (1x1 = whole image); vectorizes immediately
  const applyTemplate = (gridSize) => {
    const cellWidth = displaySize.width / gridSize;
    const cellHeight = displaySize.height / gridSize;

    const newRegions = [];
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        newRegions.push({
          id: nextId.current++,
          x: col * cellWidth,
          y: row * cellHeight,
          width: cellWidth,
          height: cellHeight,
        });
      }
    }

    setCropRegions(newRegions);
    setVectorizedResults([]);
    setIconNames({});
    setSavedIcons({});
    vectorizeRegions(newRegions, imageSize, displaySize);
  };

  // Drag on the image to draw a new crop region
  const handlePointerDown = (e) => {
    if (e.target !== imageRef.current || e.button !== 0) return;
    e.preventDefault();

    const rect = imageRef.current.getBoundingClientRect();
    const clamp = (v, max) => Math.min(Math.max(v, 0), max);
    const startX = clamp(e.clientX - rect.left, displaySize.width);
    const startY = clamp(e.clientY - rect.top, displaySize.height);

    const toRect = (ev) => {
      const x = clamp(ev.clientX - rect.left, displaySize.width);
      const y = clamp(ev.clientY - rect.top, displaySize.height);
      return { x: Math.min(x, startX), y: Math.min(y, startY), width: Math.abs(x - startX), height: Math.abs(y - startY) };
    };

    const onMove = (ev) => setDraft(toRect(ev));
    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDraft(null);
      const r = toRect(ev);
      if (r.width >= MIN_REGION && r.height >= MIN_REGION) {
        setCropRegions(prev => [...prev, { id: nextId.current++, ...r }]);
        setStale(true);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Sanitize icon name on change
  const handleIconNameChange = (cropId, value) => {
    // Convert to lowercase and remove invalid characters
    const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setIconNames({ ...iconNames, [cropId]: sanitized });
  };

  const saveIcon = async (cropId, svg) => {
    const iconName = iconNames[cropId];
    if (!iconName || !isValidIconName(iconName)) {
      setError('Icon names may only use lowercase letters, numbers, and underscores');
      return false;
    }

    setSavingIcons(prev => ({ ...prev, [cropId]: true }));
    try {
      const file = new File([new Blob([svg], { type: 'image/svg+xml' })], `${iconName}.svg`, { type: 'image/svg+xml' });
      await uploadIcon(file, iconName);
      setSavedIcons(prev => ({ ...prev, [cropId]: true }));
      return true;
    } catch (err) {
      console.error('Error saving icon:', err);
      setError(`Failed to save "${iconName}": ${err.message}`);
      return false;
    } finally {
      setSavingIcons(prev => ({ ...prev, [cropId]: false }));
    }
  };

  const finishSaving = async () => {
    await clearCustomIconsCache();
    if (onIconsSaved) onIconsSaved();
  };

  const handleSaveIcon = async (cropId, svg) => {
    setError(null);
    if (await saveIcon(cropId, svg)) await finishSaving();
  };

  const saveable = vectorizedResults.filter(r => iconNames[r.cropId]?.trim() && !savedIcons[r.cropId]);

  const handleSaveAll = async () => {
    setError(null);
    let any = false;
    for (const r of saveable) {
      if (await saveIcon(r.cropId, r.svg)) any = true;
    }
    if (any) await finishSaving();
  };

  if (!imageUrl) return null;

  return (
    <section className="generator-section generation-results">
      <h3>Crop & vectorize</h3>
      <p className="section-description">
        Drag on the image to select an icon. Drag the box to move it, drag its edges to resize.
      </p>

      <div className="crop-controls">
        <div className="template-buttons">
          <span>Split into:</span>
          <button onClick={() => applyTemplate(1)} className="btn-tertiary">Whole image</button>
          <button onClick={() => applyTemplate(2)} className="btn-tertiary">2×2</button>
          <button onClick={() => applyTemplate(3)} className="btn-tertiary">3×3</button>
          <button onClick={() => applyTemplate(4)} className="btn-tertiary">4×4</button>
        </div>
      </div>

      <div
        className="image-crop-container"
        ref={containerRef}
        onPointerDown={handlePointerDown}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Source"
          draggable={false}
          style={{
            width: `${displaySize.width}px`,
            height: `${displaySize.height}px`,
            display: 'block',
            cursor: 'crosshair',
            userSelect: 'none',
          }}
        />

        {cropRegions.map((region, i) => (
          <Rnd
            key={region.id}
            size={{ width: region.width, height: region.height }}
            position={{ x: region.x, y: region.y }}
            onDragStop={(e, d) => {
              updateCropRegion(region.id, { x: d.x, y: d.y }, { width: region.width, height: region.height });
            }}
            onResizeStop={(e, direction, ref, delta, position) => {
              updateCropRegion(region.id, position, { width: ref.offsetWidth, height: ref.offsetHeight });
            }}
            minWidth={MIN_REGION}
            minHeight={MIN_REGION}
            bounds="parent"
            className="crop-region"
            resizeHandleClasses={{
              topLeft: 'crop-handle', topRight: 'crop-handle', bottomLeft: 'crop-handle', bottomRight: 'crop-handle',
            }}
          >
            <span className="crop-region-index">{i + 1}</span>
            <button
              onClick={() => removeCropRegion(region.id)}
              onPointerDown={(e) => e.stopPropagation()}
              className="crop-region-delete"
              title="Remove crop region"
            >
              <UiIcon name="close" />
            </button>
          </Rnd>
        ))}

        {draft && (
          <div
            className="crop-region crop-region-draft"
            style={{ left: draft.x, top: draft.y, width: draft.width, height: draft.height }}
          />
        )}
      </div>

      <button
        onClick={() => vectorizeRegions(cropRegions, imageSize, displaySize)}
        disabled={loading || cropRegions.length === 0 || !stale}
        className="btn-primary"
        style={{ marginTop: '1rem' }}
      >
        {loading ? 'Vectorizing...' : stale ? `Update preview (${cropRegions.length})` : 'Preview up to date'}
      </button>

      {error && <div className="error-message">{error}</div>}

      {vectorizedResults.length > 0 && (
        <div className="vectorized-results">
          <h4>Name and save</h4>
          <div className="icon-results-list">
            {vectorizedResults.map((result) => (
              <div key={result.cropId} className="icon-result-item">
                <div
                  className="svg-preview"
                  dangerouslySetInnerHTML={{ __html: result.svg }}
                />
                <input
                  type="text"
                  placeholder="icon_name"
                  value={iconNames[result.cropId] || ''}
                  onChange={(e) => handleIconNameChange(result.cropId, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveIcon(result.cropId, result.svg); }}
                  className="icon-name-input"
                  pattern="[a-z0-9_]+"
                  title="Lowercase letters, numbers, and underscores only"
                  disabled={savedIcons[result.cropId]}
                />
                <button
                  onClick={() => handleSaveIcon(result.cropId, result.svg)}
                  disabled={savingIcons[result.cropId] || savedIcons[result.cropId] || !iconNames[result.cropId]?.trim()}
                  className="btn-primary"
                >
                  {savedIcons[result.cropId] ? 'Saved' : savingIcons[result.cropId] ? 'Saving...' : 'Save'}
                </button>
              </div>
            ))}
          </div>
          {vectorizedResults.length > 1 && (
            <button onClick={handleSaveAll} disabled={saveable.length === 0} className="btn-primary" style={{ marginTop: '1rem' }}>
              Save all named ({saveable.length})
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default GenerationResults;
